// Enhanced MediaGallery with proper image loading after variant changes
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
      });
      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${event.detail.currentElement.dataset.mediaId}"]`);
      this.setActiveThumbnail(thumbnail);

      // Update mobile slider when desktop slider changes
      if (window.innerWidth <= 768) {
        this.updateMobileSliderFromDesktop(event.detail.currentElement.dataset.mediaId);
      }
    }

    // Extract section ID from media ID
    getSectionIdFromMediaId(mediaId) {
      if (mediaId && mediaId.includes('-')) {
        const parts = mediaId.split('-');
        if (parts.length >= 2) {
          const sectionId = parts.slice(0, -1).join('-');
          return sectionId;
        }
      }
      return null;
    }

    // Force image reload and ensure proper loading
    forceImageReload(slideElement) {
      const images = slideElement.querySelectorAll('img');
      const videos = slideElement.querySelectorAll('video');

      images.forEach(img => {
        if (!img.complete || img.naturalWidth === 0) {
          const src = img.src;
          img.onload = () => {
            // Image reloaded successfully
          };
          img.onerror = () => {
            // Image failed to reload
          };
          img.src = '';
          img.src = src;
        }
      });

      videos.forEach(video => {
        video.load();
      });
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) {
        return;
      }

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({
            left: activeMedia.offsetLeft
          });
        }
        if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
          activeMedia.scrollIntoView({
            behavior: 'smooth'
          });
        }
      });
      this.playActiveMedia(activeMedia);

      // Enhanced mobile slider logic with proper image loading
      if (window.innerWidth <= 768) {
        // Add delay to ensure DOM is ready
        setTimeout(() => {
          this.updateMobileSlider(mediaId);
        }, 100);
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      if (activeThumbnail) {
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }

    updateMobileSlider(mediaId) {
      const sectionId = this.getSectionIdFromMediaId(mediaId);

      if (!sectionId) {
        return;
      }

      const mobileSlider = document.querySelector(`#MobileSliderTrack-${sectionId}`);

      if (!mobileSlider) {
        return;
      }

      const slides = mobileSlider.querySelectorAll('.mobile-slide');

      if (slides.length === 0) {
        return;
      }

      // Find the correct slide by matching the media ID
      const activeSlide = Array.from(slides).find(slide => {
        const slideMediaId = slide.getAttribute('data-media-id');
        return slideMediaId === mediaId;
      });

      if (activeSlide) {
        // Remove active class from all slides
        slides.forEach(slide => slide.classList.remove('is-active'));
        // Add active class to the correct slide
        activeSlide.classList.add('is-active');

        // Force image reload for the active slide
        this.forceImageReload(activeSlide);

        // Calculate the correct scroll position
        const slideWidth = activeSlide.offsetWidth;
        const slideIndex = Array.from(slides).indexOf(activeSlide);
        const scrollPosition = slideIndex * slideWidth;

        // CRITICAL: Reset any CSS transforms that might interfere
        mobileSlider.style.transform = 'translateX(0%)';
        mobileSlider.style.transition = 'none';

        // Reset scroll position with immediate and then smooth scroll
        mobileSlider.scrollTo({
          left: scrollPosition,
          behavior: 'auto' // First scroll immediately
        });

        // Then smooth scroll to ensure proper positioning
        setTimeout(() => {
          mobileSlider.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
          // Re-enable transitions after scroll
          mobileSlider.style.transition = '';
        }, 50);

        // Update dots after scroll
        setTimeout(() => {
          this.updateMobileDots(sectionId, slides, activeSlide);
        }, 100);

        // Force a repaint to ensure images are visible
        activeSlide.style.opacity = '0.99';
        setTimeout(() => {
          activeSlide.style.opacity = '1';
        }, 10);

      }
    }

    updateMobileDots(sectionId, slides, activeSlide) {
      const dots = document.querySelectorAll(`#MobileDots-${sectionId} .mobile-dot`);

      if (dots.length === 0) return;

      // Remove active class from all dots
      dots.forEach(dot => dot.classList.remove('active'));

      // Find the correct dot index
      const slideIndex = Array.from(slides).indexOf(activeSlide);

      if (slideIndex >= 0 && dots[slideIndex]) {
        dots[slideIndex].classList.add('active');
      }
    }

    updateMobileSliderFromDesktop(mediaId) {
      // Add small delay to ensure synchronization
      setTimeout(() => {
        this.updateMobileSlider(mediaId);
      }, 50);
    }

    resetMobileSlider(sectionId = null) {
      if (window.innerWidth <= 768) {
        if (!sectionId) {
          const galleryId = this.id;
          if (galleryId && galleryId.includes('MediaGallery-')) {
            sectionId = galleryId.replace('MediaGallery-', '');
          }
        }

        if (!sectionId) {
          return;
        }

        const mobileSlider = document.querySelector(`#MobileSliderTrack-${sectionId}`);
        if (mobileSlider) {
          const activeSlide = mobileSlider.querySelector('.mobile-slide.is-active');

          if (activeSlide) {
            // Force image reload for the active slide
            this.forceImageReload(activeSlide);

            // Calculate proper scroll position
            const slides = mobileSlider.querySelectorAll('.mobile-slide');
            const slideIndex = Array.from(slides).indexOf(activeSlide);
            const slideWidth = activeSlide.offsetWidth;
            const scrollPosition = slideIndex * slideWidth;

            // CRITICAL: Reset any CSS transforms that might interfere
            mobileSlider.style.transform = 'translateX(0%)';
            mobileSlider.style.transition = 'none';

            // Reset scroll position
            mobileSlider.scrollTo({
              left: scrollPosition,
              behavior: 'auto'
            });

            // Then smooth scroll
            setTimeout(() => {
              mobileSlider.scrollTo({
                left: scrollPosition,
                behavior: 'smooth'
              });
              // Re-enable transitions after scroll
              mobileSlider.style.transition = '';
            }, 50);

            // Update dots
            setTimeout(() => {
              this.updateMobileDots(sectionId, slides, activeSlide);
            }, 100);

          }
        }
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button').removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({
        left: thumbnail.offsetLeft
      });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
